import { NgClass } from "@angular/common";
import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { BlockComponent } from "@components/container-block/block.component";
import { IconButtonComponent } from "@components/icon-button/icon-button.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent } from "@components/list/list.component";
import { PageComponent } from "@components/page/page.component";
import { Subject, interval } from "rxjs";
import { startWith, takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { RebusQueueDTO } from "./rebus.models";
import { RebusService } from "./rebus.service";

@Component({
    selector: "rask-rebus",
    templateUrl: "./rebus.component.html",
    styleUrls: ["./rebus.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        BlockComponent,
        CheckboxComponent,
        FormsModule,
        ListComponent,
        ListColumnDirective,
        RouterLink,
        IconButtonComponent,
        NgClass
    ]
})
export class RebusComponent implements OnInit, OnDestroy {
    allQueues: RebusQueueDTO[];
    autoRefresh: boolean;
    interval: any;
    nonZeroQueues = true;
    queuesFiltered: RebusQueueDTO[];

    private readonly busyService = inject(BusyService);
    private readonly modalService = inject(RaskModalService);
    private readonly rebusService = inject(RebusService);
    private readonly unsubscribe$: Subject<void>;

    constructor() {
        this.unsubscribe$ = new Subject<void>();
    }

    askAndPurgeQueue(type: string, queue: string, subQueue: string): void {
        const subQueuePath = subQueue ? ` - ${subQueue}` : "";
        this.modalService.confirm(new ModalSettings("Tömma Rebus kö", `Är du säker på att du vill tömma kö '${queue}${subQueuePath}'`)).subscribe(modalResult => {
            if (modalResult === ConfirmDialogResponse.save) {
                this.busyService.addWork(this.rebusService.purgeQueue(type, queue, subQueue)).subscribe((result) => {
                    this.getQueues();
                });
            }
        });
    }

    getQueues(): void {
        this.busyService.addWork(this.rebusService.getQueues())
            .subscribe(queues => {
                this.setQueues(queues);
            });
    }

    hasErrors(queue: RebusQueueDTO): boolean {
        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (queue.errorCount > 0) {
            return true;
        }
        return false;
    }

    hasMessages(queue: RebusQueueDTO): boolean {
        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (queue.messageCount > 0) {
            return true;
        }
        return false;
    }

    ngOnDestroy(): void {
        this.unsubscribe();
    }

    async ngOnInit(): Promise<void> {
        this.getQueues();
    }

    nonZeroQueuesChange($event): void {
        this.setQueues(this.allQueues);
    }

    onAutoRefreshChange($event): void {
        if ($event) {
            interval(30 * 1000)
                .pipe(startWith(0))
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe(x => {
                    this.busyService.addWork(this.rebusService.getQueues())
                        .subscribe(
                            {
                                next: queues => {
                                    this.setQueues(queues);
                                },
                                error: (error) => {
                                    this.unsubscribe();
                                    this.autoRefresh = false;
                                }
                            }
                        );
                });
        } else {
            this.unsubscribe();
        }
    }

    purgeErrorQueue(queue: RebusQueueDTO): void {
        this.askAndPurgeQueue(queue.type, `${queue.queue}`, queue.errorSubQueue);
    }

    purgeQueue(queue: RebusQueueDTO): void {
        this.askAndPurgeQueue(queue.type, queue.queue, null);
    }

    setQueues(queues: RebusQueueDTO[]) {
        this.allQueues = queues;
        this.queuesFiltered = queues.filter(x => (x.errorCount > 0 || x.messageCount > 0) || !this.nonZeroQueues);
    }

    private unsubscribe(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
